import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, X } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const texts = {
  it: {
    title: 'Informativa sui Cookie',
    body: 'Questo sito utilizza cookie tecnici necessari al funzionamento e cookie analitici/di profilazione per migliorare la tua esperienza. Puoi accettare tutti i cookie oppure scegliere di utilizzare solo quelli necessari.',
    acceptAll: 'Accetta tutti',
    onlyNecessary: 'Solo necessari',
    moreInfo: 'Maggiori informazioni',
  },
  en: {
    title: 'Cookie Notice',
    body: 'This site uses necessary technical cookies and analytics/profiling cookies to improve your experience. You can accept all cookies or choose to use only the necessary ones.',
    acceptAll: 'Accept all',
    onlyNecessary: 'Only necessary',
    moreInfo: 'More information',
  },
};

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const { lang } = useLanguage();
  const t = texts[lang] || texts.it;

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) setVisible(true);

    const handleReopen = () => setVisible(true);
    window.addEventListener('reopen-cookie-banner', handleReopen);
    return () => window.removeEventListener('reopen-cookie-banner', handleReopen);
  }, []);

  const handleAccept = (type) => {
    const value = JSON.stringify({ type, date: new Date().toISOString() });
    localStorage.setItem('cookie_consent', value);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] animate-in slide-in-from-bottom duration-500"
      data-testid="cookie-banner"
    >
      <div className="bg-gray-900/95 backdrop-blur-xl border-t border-gray-700/50 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-[#0066b2]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Shield className="w-5 h-5 text-[#0066b2]" />
              </div>
              <div className="min-w-0">
                <p className="text-white font-semibold text-sm mb-1" style={{ fontFamily: 'Space Grotesk' }}>
                  {t.title}
                </p>
                <p className="text-gray-300 text-xs leading-relaxed">
                  {t.body}{' '}
                  <Link
                    to="/cookie-policy"
                    className="text-[#0066b2] hover:underline font-medium"
                    data-testid="cookie-banner-more-info"
                  >
                    {t.moreInfo}
                  </Link>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
              <button
                onClick={() => handleAccept('necessary')}
                className="flex-1 sm:flex-initial px-4 py-2.5 text-xs font-semibold text-gray-300 bg-gray-700/60 hover:bg-gray-600/80 border border-gray-600 rounded-lg transition-colors"
                data-testid="cookie-banner-reject"
              >
                {t.onlyNecessary}
              </button>
              <button
                onClick={() => handleAccept('all')}
                className="flex-1 sm:flex-initial px-5 py-2.5 text-xs font-semibold text-white bg-[#0066b2] hover:bg-[#0055a0] rounded-lg transition-colors shadow-lg shadow-[#0066b2]/25"
                data-testid="cookie-banner-accept"
              >
                {t.acceptAll}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
